import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import { toast } from '../ui/Toast';
import { Icon, Upload, MapPin, CalendarDays, CheckCircle2, X, Plane, BedDouble, Ticket, Lightbulb, Luggage, DollarSign, FileText } from '../ui/Icon';
import { importApi, extractAllWithProgress, importPlanCounts, type MaterializeResponse, type ExtractedTripPlan } from '../../api/import.api';
import ImportLanguageNotice from './ImportLanguageNotice';
import { persistVenueProposal, persistDayPlanProposal } from '../../api/aiProposals';
import {
  persistFlightProposal, persistHotelProposal, persistBookingProposal,
  persistTipProposal, persistExpenseProposal, persistPackingProposals,
  saveImportedDocuments,
} from '../../api/importPersist';
import { withinLimit } from '../../utils/uploadLimits';
import { hidesTab } from '../../utils/tripPermissions';
import { useTripContext } from '../../pages/trip/TripDetailPage';

const ACCEPT = '.pdf,.docx,.xlsx,.xls,.csv,.txt,image/*';
type Phase = 'select' | 'extracting' | 'review' | 'saving';

interface Props {
  tripId: string;
  onClose: () => void;
  onImported: () => void;
}

const EMPTY: MaterializeResponse = {
  venues: [], dayPlans: [], flights: [], hotels: [], bookings: [], tips: [], packing: [], expenses: [],
  mapsQuotaExceeded: null, languageMismatch: null,
};

const sub = (id: string, i: number) => `${id}#${i}`;

/** Every proposal starts selected — reviewing means unticking what you don't want. */
function selectAll(res: MaterializeResponse): Set<string> {
  const keys = new Set<string>();
  res.venues.forEach(v => keys.add(v.pendingId));
  res.dayPlans.forEach(d => d.activities.forEach((_, i) => keys.add(sub(d.pendingId, i))));
  res.flights.forEach(f => keys.add(f.pendingId));
  res.hotels.forEach(h => keys.add(h.pendingId));
  res.bookings.forEach(b => keys.add(b.pendingId));
  res.tips.forEach(tp => keys.add(tp.pendingId));
  res.packing.forEach(p => p.items.forEach((_, i) => keys.add(sub(p.pendingId, i))));
  res.expenses.forEach(e => keys.add(e.pendingId));
  return keys;
}

function countProposals(res: MaterializeResponse): number {
  return res.venues.length + res.dayPlans.reduce((s, d) => s + d.activities.length, 0)
    + res.flights.length + res.hotels.length + res.bookings.length + res.tips.length
    + res.packing.reduce((s, p) => s + p.items.length, 0) + res.expenses.length;
}

function fmtDate(iso?: string): string { return iso ? iso.slice(0, 10) : ''; }

/** Just the host, as a hint that a booking link was recovered from the document. */
function linkHost(url?: string): string | undefined {
  if (!url) return undefined;
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return undefined; }
}

export default function ImportDocumentModal({ tripId, onClose, onImported }: Props) {
  const { t } = useTranslation();
  const { trip } = useTripContext();
  // ADR-0038: the agency cannot write the suitcase or the expenses, so the
  // review does not offer what would come back 403 halfway through saving.
  const forViewer = (res: MaterializeResponse): MaterializeResponse => ({
    ...res,
    packing: hidesTab(trip, 'packing') ? [] : res.packing,
    expenses: hidesTab(trip, 'expenses') ? [] : res.expenses,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('select');
  const [error, setError] = useState<string | null>(null);
  const [staged, setStaged] = useState<File[]>([]);
  const [data, setData] = useState<MaterializeResponse>(EMPTY);
  // One selection set keyed by pendingId (simple items) or `${pendingId}#${i}`
  // for sub-items (day activities, packing items). pendingIds are globally unique.
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [partial, setPartial] = useState<ExtractedTripPlan | null>(null);
  // Kept after extraction so "translate" can re-materialize from the same plan
  // without re-reading the documents (that's the call that costs a credit).
  const [plan, setPlan] = useState<ExtractedTripPlan | null>(null);
  const [translating, setTranslating] = useState(false);

  const isSel = (k: string) => sel.has(k);
  const toggle = (k: string) => setSel(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const { accepted, errors } = withinLimit(Array.from(fileList), 'document');
    setError(errors[0] ?? null);
    if (accepted.length === 0) return;
    // Accumulate across picks (cap at 10 documents per import), de-duping by
    // name+size. Each one is sent in its own request, so the server's per-request
    // file limit is not what caps this.
    setStaged(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of accepted) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) { seen.add(key); merged.push(f); }
      }
      return merged.slice(0, 10);
    });
  }

  function removeStaged(i: number) {
    setStaged(prev => prev.filter((_, idx) => idx !== i));
  }

  async function analyze() {
    if (staged.length === 0) return;
    setError(null);
    setProgress({ current: 0, total: staged.length });
    setPartial(null);
    setPhase('extracting');
    try {
      const { plan: extracted } = await extractAllWithProgress(staged, (processed, total, planSoFar) => {
        setProgress({ current: processed, total });
        setPartial(planSoFar);
      });
      const res = await importApi.materialize(tripId, extracted, { autoAccept: false });

      if (countProposals(res) === 0) {
        setPhase('select');
        setError(t('import.errors.nothingFound'));
        return;
      }

      setPlan(extracted);
      setData(forViewer(res));
      setSel(selectAll(forViewer(res)));
      setPhase('review');
      if (res.mapsQuotaExceeded) toast(t('import.mapsQuotaWarning'), 'info');
    } catch (err) {
      setPhase('select');
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 402 ? t('import.errors.insufficientCredits')
        : status === 429 ? t('import.errors.rateLimited')
        : t('import.errors.extractFailed'));
    }
  }

  // Re-materializes the same plan with its prose translated. Selections are
  // rebuilt (the proposals come back with fresh pendingIds), which is why the
  // offer is taken before ticking anything rather than at save time. The second
  // materialize costs nothing at Google: place names and zones are exactly what
  // they were, so every resolution and details lookup hits the cache it just
  // filled.
  async function translate() {
    if (!plan) return;
    setTranslating(true);
    try {
      const res = await importApi.materialize(tripId, plan, { autoAccept: false, translate: true });
      setData(forViewer(res));
      setSel(selectAll(forViewer(res)));
      toast(t('import.language.done'), 'success');
    } catch {
      toast(t('import.language.failed'), 'error');
    } finally {
      setTranslating(false);
    }
  }

  const selectedCount = sel.size;

  async function handleImport() {
    if (selectedCount === 0) return;
    setPhase('saving');
    let added = 0;
    try {
      for (const v of data.venues) if (isSel(v.pendingId)) { await persistVenueProposal(tripId, v, 'IMPORT'); added++; }
      for (const d of data.dayPlans) {
        const activities = d.activities.filter((_, i) => isSel(sub(d.pendingId, i)));
        if (activities.length) { await persistDayPlanProposal(tripId, { ...d, activities }, 'IMPORT'); added += activities.length; }
      }
      for (const f of data.flights) if (isSel(f.pendingId)) { await persistFlightProposal(tripId, f); added++; }
      for (const h of data.hotels) if (isSel(h.pendingId)) { await persistHotelProposal(tripId, h); added++; }
      for (const b of data.bookings) if (isSel(b.pendingId)) { await persistBookingProposal(tripId, b); added++; }
      for (const tp of data.tips) if (isSel(tp.pendingId)) { await persistTipProposal(tripId, tp); added++; }
      const packGroups = data.packing
        .map(p => ({ ...p, items: p.items.filter((_, i) => isSel(sub(p.pendingId, i))) }))
        .filter(p => p.items.length > 0);
      if (packGroups.length) { await persistPackingProposals(tripId, packGroups); added += packGroups.reduce((s, p) => s + p.items.length, 0); }
      for (const e of data.expenses) if (isSel(e.pendingId)) { await persistExpenseProposal(tripId, e); added++; }

      // Keep the originals in the doc manager (best-effort — the import already
      // committed, so a storage hiccup must not surface as a save failure).
      saveImportedDocuments(tripId, staged, t('import.savedDocsNote')).catch(() => {});

      toast(t('import.added', { count: added }), 'success');
      onImported();
      onClose();
    } catch {
      setPhase('review');
      toast(t('import.errors.saveFailed'), 'error');
    }
  }

  return (
    <Modal open onClose={onClose} title={t('import.title')} size="lg">
      {(phase === 'select' || phase === 'extracting') && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('import.description')}</p>

          {phase === 'extracting' ? (
            <div className="w-full flex flex-col items-center justify-center gap-2.5 py-10 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 px-4">
              <Spinner size="md" />
              {partial && importPlanCounts(partial).length > 0 ? (
                <div className="flex flex-wrap justify-center gap-1.5">
                  {importPlanCounts(partial).map(c => (
                    <span key={c.key} className="text-[12px] font-medium px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                      {t(c.key, { count: c.count })}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('import.extracting')}</span>
              )}
              {progress && progress.total > 1 && (
                <div className="w-full max-w-xs mt-1 space-y-1.5">
                  <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-400 text-center">{t('import.docProgress', { current: progress.current, total: progress.total })}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                type="button" onClick={() => inputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-teal-400 hover:bg-teal-50/40 dark:hover:bg-teal-900/10 transition-colors cursor-pointer"
              >
                <Icon as={Upload} size={26} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{staged.length > 0 ? t('import.addMore') : t('import.dropHint')}</span>
                <span className="text-xs text-slate-400">{t('import.formats')}</span>
              </button>

              {staged.length > 0 && (
                <ul className="space-y-1">
                  {staged.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800">
                      <Icon as={FileText} size={15} className="text-slate-400 shrink-0" />
                      <span className="flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{f.name}</span>
                      <button type="button" onClick={() => removeStaged(i)} className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer" aria-label={t('common.remove')}>
                        <Icon as={X} size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <button type="button" className="btn-primary w-full" disabled={staged.length === 0} onClick={analyze}>
                {t('import.analyze', { count: staged.length })}
              </button>
            </>
          )}

          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden"
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      )}

      {(phase === 'review' || phase === 'saving') && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('import.reviewHint')}</p>

          {data.languageMismatch && (
            <ImportLanguageNotice
              document={data.languageMismatch.document}
              trip={data.languageMismatch.trip}
              translating={translating}
              onTranslate={translate}
            />
          )}

          {data.venues.length > 0 && (
            <Section icon={MapPin} label={t('import.placesSection', { count: data.venues.length })}>
              {data.venues.map(v => (
                <Row key={v.pendingId} checked={isSel(v.pendingId)} onToggle={() => toggle(v.pendingId)}
                  title={v.name} subtitle={[v.address || v.zone, linkHost(v.bookingUrl)].filter(Boolean).join(' · ') || undefined} />
              ))}
            </Section>
          )}

          {data.dayPlans.map(d => (
            <Section key={d.pendingId} icon={CalendarDays}
              label={d.dayNumber ? t('import.daySection', { number: d.dayNumber }) : t('import.daySectionNoNumber')}>
              {d.activities.map((a, i) => (
                <Row key={i} checked={isSel(sub(d.pendingId, i))} onToggle={() => toggle(sub(d.pendingId, i))}
                  title={a.title} subtitle={[a.startTime, a.venueName, linkHost(a.bookingUrl ?? undefined)].filter(Boolean).join(' · ') || undefined} />
              ))}
            </Section>
          ))}

          {data.flights.length > 0 && (
            <Section icon={Plane} label={t('import.flightsSection', { count: data.flights.length })}>
              {data.flights.map(f => (
                <Row key={f.pendingId} checked={isSel(f.pendingId)} onToggle={() => toggle(f.pendingId)}
                  title={`${f.legs[0]?.fromAirport} → ${f.legs[f.legs.length - 1]?.toAirport}`}
                  subtitle={[fmtDate(f.legs[0]?.departureTime), f.legs[0]?.airline, f.bookingRef].filter(Boolean).join(' · ') || undefined} />
              ))}
            </Section>
          )}

          {data.hotels.length > 0 && (
            <Section icon={BedDouble} label={t('import.hotelsSection', { count: data.hotels.length })}>
              {data.hotels.map(h => (
                <Row key={h.pendingId} checked={isSel(h.pendingId)} onToggle={() => toggle(h.pendingId)}
                  title={h.name}
                  subtitle={[`${fmtDate(h.checkIn)} – ${fmtDate(h.checkOut)}`, t('import.nightsCount', { count: h.nights }), linkHost(h.bookingUrl)].filter(Boolean).join(' · ')} />
              ))}
            </Section>
          )}

          {data.bookings.length > 0 && (
            <Section icon={Ticket} label={t('import.bookingsSection', { count: data.bookings.length })}>
              {data.bookings.map(b => (
                <Row key={b.pendingId} checked={isSel(b.pendingId)} onToggle={() => toggle(b.pendingId)}
                  title={b.title} subtitle={[fmtDate(b.date), b.startTime, b.venue, linkHost(b.bookingUrl)].filter(Boolean).join(' · ') || undefined} />
              ))}
            </Section>
          )}

          {data.tips.length > 0 && (
            <Section icon={Lightbulb} label={t('import.tipsSection', { count: data.tips.length })}>
              {data.tips.map(tp => (
                <Row key={tp.pendingId} checked={isSel(tp.pendingId)} onToggle={() => toggle(tp.pendingId)}
                  title={tp.content} subtitle={tp.category} />
              ))}
            </Section>
          )}

          {data.packing.map(p => (
            <Section key={p.pendingId} icon={Luggage} label={t('import.packingSection', { section: p.section })}>
              {p.items.map((item, i) => (
                <Row key={i} checked={isSel(sub(p.pendingId, i))} onToggle={() => toggle(sub(p.pendingId, i))} title={item} />
              ))}
            </Section>
          ))}

          {data.expenses.length > 0 && (
            <Section icon={DollarSign} label={t('import.expensesSection', { count: data.expenses.length })}>
              {data.expenses.map(e => (
                <Row key={e.pendingId} checked={isSel(e.pendingId)} onToggle={() => toggle(e.pendingId)}
                  title={e.description} subtitle={`${e.amount} ${e.currency} · ${fmtDate(e.date)}`} />
              ))}
            </Section>
          )}

          <div className="flex items-center gap-3 pt-2 sticky bottom-0 bg-white dark:bg-slate-900 pb-1">
            <button type="button" className="btn-primary flex-1" disabled={selectedCount === 0 || phase === 'saving'} onClick={handleImport}>
              {phase === 'saving'
                ? <Spinner size="sm" />
                : <span className="flex items-center justify-center gap-1.5"><Icon as={CheckCircle2} size={15} />{t('import.addSelected', { count: selectedCount })}</span>}
            </button>
            <button type="button" className="btn-secondary" disabled={phase === 'saving'} onClick={onClose}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Section({ icon, label, children }: { icon: typeof MapPin; label: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
        <Icon as={icon} size={13} /> {label}
      </h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({ checked, onToggle, title, subtitle }: {
  checked: boolean; onToggle: () => void; title: string; subtitle?: string;
}) {
  return (
    <button type="button" onClick={onToggle}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer">
      <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
        checked ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
        {checked ? <Icon as={CheckCircle2} size={13} /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm truncate ${checked ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400 line-through'}`}>{title}</span>
        {subtitle && <span className="block text-[11px] text-slate-400 truncate">{subtitle}</span>}
      </span>
      {!checked && <Icon as={X} size={13} className="text-slate-300 shrink-0" />}
    </button>
  );
}
